import type { Components } from '@/api/generated/api';
import type { UseQueryReturnType } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import {
  fetchSchoolSubject,
  fetchSchoolSubjectsForClass,
  fetchTenantSubjectTemplates,
  canUserAccessAllSubjectsInClass,
} from '@/api/resources/schoolSubject.api';
import { schoolSubjectKeys } from './schoolSubject.keys';

type SubjectId = MaybeRefOrGetter<number | undefined>;

export const useSchoolSubjectsQuery = (
  classId: SubjectId,
): UseQueryReturnType<Components.Schemas.SchoolSubjectDto[], Error> =>
  useQueryWithToast({
    queryKey: computed(() => schoolSubjectKeys.classList(toValue(classId))),
    enabled: computed(() => toValue(classId) != null),
    queryFn: () => fetchSchoolSubjectsForClass({ classId: toValue(classId)! }),
  });

export const useSchoolSubjectAccessQuery = (classId: SubjectId): UseQueryReturnType<boolean, Error> =>
  useQueryWithToast({
    queryKey: computed(() => schoolSubjectKeys.canAccessAll(toValue(classId))),
    enabled: computed(() => toValue(classId) != null),
    queryFn: () => canUserAccessAllSubjectsInClass({ classId: toValue(classId)! }),
  });

// The administration grid needs all levels for its empty/setup state and cross-level moves.
export const useTenantSchoolSubjectTemplatesQuery = (
  tenantId: SubjectId,
): UseQueryReturnType<Components.Schemas.SchoolSubjectDto[], Error> =>
  useQueryWithToast({
    queryKey: computed(() => schoolSubjectKeys.tenantTemplates(toValue(tenantId))),
    enabled: computed(() => toValue(tenantId) != null),
    queryFn: () => fetchTenantSubjectTemplates(),
  });

export const useSchoolSubjectQuery = (
  subjectId: SubjectId,
): UseQueryReturnType<Components.Schemas.SchoolSubjectDto, Error> =>
  useQueryWithToast({
    queryKey: computed(() => schoolSubjectKeys.detail(toValue(subjectId))),
    enabled: computed(() => toValue(subjectId) != null),
    queryFn: () => fetchSchoolSubject(toValue(subjectId)!),
  });
